import { Dispatch, SetStateAction, useCallback, useState } from 'react';

import { Box, DropdownMenu, Icon } from '@betterment/samba-react';
import { isEmpty } from 'es-toolkit/compat';

import { Section } from './MenuContents/DropdownMenuContent';

import { accountsByType } from '../../../../shared/utils';
import { PerformanceAccountsQuery } from '../../../graphql/performanceAccounts.generated';

export type AccountsFilterProps = {
  accounts: PerformanceAccountsQuery['performance']['accounts'];
  selectedAccountIds: Set<string>;
  selectedSetter: Dispatch<SetStateAction<Set<string>>>;
};

export const AccountsFilter: React.FC<AccountsFilterProps> = ({
  accounts,
  selectedSetter,
  selectedAccountIds = new Set(),
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { cashAccounts, investingAccounts } = accountsByType(accounts);

  const toggleAccountSelection = useCallback(
    (accountId: string) => {
      const updatedIds = new Set(selectedAccountIds);
      if (updatedIds.has(accountId)) {
        updatedIds.delete(accountId);
      } else {
        updatedIds.add(accountId);
      }
      selectedSetter(updatedIds);
    },
    [selectedAccountIds, selectedSetter]
  );

  if (accounts.length <= 1) {
    return null;
  }

  return (
    <DropdownMenu onOpenChange={setIsExpanded} modal={false}>
      <DropdownMenu.Trigger asChild={true}>
        <Box css={{ display: 'flex', alignItems: 'center', pointerEvents: 'auto', cursor: 'pointer' }}>
          Filter accounts
          <Icon
            name="CaretDown"
            size="small"
            color="icon"
            css={{
              transition: 'transform 0.1s ease-in-out',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Box>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        align="start"
        css={{
          px: 'space.300',
          py: 'space.300',
          maxHeight: '45vh',
          overflowY: 'scroll',
        }}
      >
        <Section
          showSeparator={false}
          accounts={investingAccounts}
          selectedAccountIds={selectedAccountIds}
          toggleAccountSelection={toggleAccountSelection}
          header="Investing"
        />
        <Section
          showSeparator={!isEmpty(investingAccounts)}
          accounts={cashAccounts}
          selectedAccountIds={selectedAccountIds}
          toggleAccountSelection={toggleAccountSelection}
          header="Cash"
        />
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};
